import { Flex, useDisclosure, Button, Text, Box } from "@chakra-ui/react";
import OrganizationFormModal from "@root/components/superAdminDashboard/organizationFormModal";
import { StyledAdminLayoutParent } from "@root/components/adminDashboard/style";
import { CustomLoader } from "@root/components/common/loaders/CustomLoader";
import { CustomPopup } from "@root/components/common/modals/CustomPopup";
import { Headers } from "@root/components/userMangement/components/headers";
import { DataTable } from "@root/components/common/datatable/DataTable";
import { OrganizationsTableColumns } from "@root/components/superAdminDashboard/organizationsTableColumns";
import { useState, useEffect } from "react";
import { OrganizationsService } from "@root/services/superAdmin/organizations";
import { showAlert } from "@root/store/slices/features";
import { useDispatch } from "react-redux";
import { TOAST_VARIANT, ACTION } from "@root/constants";
import { useTranslation } from "react-i18next";

const organizationsService = new OrganizationsService();

export const OrganizationsScreen = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const dispatch = useDispatch();
    const { t } = useTranslation("common");

    const [organizations, setOrganizations] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedOrganization, setSelectedOrganization] = useState<any>(null);
    const [deletePopup, setDeletePopup] = useState(false);
    const [organizationToDelete, setOrganizationToDelete] = useState<any>(null);

    const pageSize = 10;

    useEffect(() => {
        fetchOrganizations();
    }, [currentPage]);

    const fetchOrganizations = async () => {
        setLoading(true);
        try {
            const response = await organizationsService.fetchOrganizations({
                page: currentPage,
                limit: pageSize,
            });
            if (response?.data?.data) {
                const data = response.data.data;
                setOrganizations(Array.isArray(data) ? data : data.rows || []);
                setTotalCount(data.total || data.length || 0);
            }
        } catch (error: any) {
            dispatch(
                showAlert({
                    message: error?.response?.data?.error || t("superAdmin.fetchFailed"),
                    type: TOAST_VARIANT.ERROR,
                })
            );
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setSelectedOrganization(null);
        onOpen();
    };

    const handleEdit = (organization: any) => {
        setSelectedOrganization(organization);
        onOpen();
    };

    const handleDelete = (organization: any) => {
        setOrganizationToDelete(organization);
        setDeletePopup(true);
    };

    const confirmDelete = async () => {
        if (!organizationToDelete || isDeleting) return;

        setIsDeleting(true);
        try {
            await organizationsService.deleteOrganization(organizationToDelete.id);
            dispatch(
                showAlert({
                    message: t("superAdmin.organizationDeleted"),
                    type: TOAST_VARIANT.SUCCESS,
                })
            );
            setDeletePopup(false);
            setOrganizationToDelete(null);
            fetchOrganizations();
        } catch (error: any) {
            // Extract error message from API response, checking multiple possible locations
            const apiErrorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.response?.data?.userMessage ||
                error?.message ||
                null;

            dispatch(
                showAlert({
                    message: apiErrorMessage || t("superAdmin.deleteFailed"),
                    type: TOAST_VARIANT.ERROR,
                })
            );
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSubmit = async (formData: FormData) => {
        setIsSubmitting(true);
        try {
            if (selectedOrganization) {
                await organizationsService.updateOrganization(
                    selectedOrganization.id,
                    formData
                );
                dispatch(
                    showAlert({
                        message: t("superAdmin.organizationUpdated"),
                        type: TOAST_VARIANT.SUCCESS,
                    })
                );
            } else {
                await organizationsService.createOrganization(formData);
                dispatch(
                    showAlert({
                        message: t("superAdmin.organizationCreated"),
                        type: TOAST_VARIANT.SUCCESS,
                    })
                );
            }
            onClose();
            setSelectedOrganization(null);
            fetchOrganizations();
        } catch (error: any) {
            dispatch(
                showAlert({
                    message:
                        error?.response?.data?.error ||
                        (selectedOrganization ? t("superAdmin.updateFailed") : t("superAdmin.createFailed")),
                    type: TOAST_VARIANT.ERROR,
                })
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleActions = (action: string, organization: any) => {
        if (action === ACTION.EDIT) {
            handleEdit(organization);
        } else if (action === ACTION.DELETE) {
            handleDelete(organization);
        }
    };

    return (
        <StyledAdminLayoutParent>
            <Flex flexDir={"column"} gap={"16px"}>
                <Headers
                    title={t("superAdmin.organizations")}
                    subheading={t("superAdmin.organizationsSubtitle")}
                    onAdd={handleCreate}
                    showFileManagement={false}
                    action={true}
                />
                <OrganizationFormModal
                    isOpen={isOpen}
                    onClose={onClose}
                    onSubmit={handleSubmit}
                    organization={selectedOrganization}
                    isLoading={isSubmitting}
                />
                {loading ? (
                    <CustomLoader />
                ) : (
                    <Box>
                        <DataTable
                            columns={OrganizationsTableColumns(handleActions, t)}
                            data={organizations}
                            showPagination={true}
                            page={currentPage}
                            pageCount={totalCount}
                            pageSize={pageSize}
                            handlePageChange={handlePageChange}
                        />
                    </Box>
                )}
            </Flex>
            <CustomPopup
                open={deletePopup}
                onClose={() => {
                    if (!isDeleting) {
                        setDeletePopup(false);
                        setOrganizationToDelete(null);
                    }
                }}
                title={t("superAdmin.deleteOrganization")}
                borderRadius={"16px"}
                bodyPadding="20px"
            >
                <Flex direction="column" gap={4}>
                    <Text>
                        {t("superAdmin.deleteConfirm", { name: organizationToDelete?.name })}
                    </Text>
                    <Flex gap={2} justifyContent="flex-end">
                        <Button
                            variant="outline"
                            onClick={() => {
                                if (!isDeleting) {
                                    setDeletePopup(false);
                                    setOrganizationToDelete(null);
                                }
                            }}
                            isDisabled={isDeleting}
                        >
                            {t("admin.cancel")}
                        </Button>
                        <Button
                            bg="red.500"
                            color="white"
                            onClick={confirmDelete}
                            isLoading={isDeleting}
                            loadingText={t("translate.delete")}
                            isDisabled={isDeleting}
                        >
                            {t("translate.delete")}
                        </Button>
                    </Flex>
                </Flex>
            </CustomPopup>
        </StyledAdminLayoutParent>
    );
};
